import Styles from './Social.module.scss'
import { Section } from './Section'
import { Button } from 'blaze-ui/Button'
import { Text } from 'blaze-ui/Text/Text'
import { LinkAngled, Mailchimp, Settings } from 'blaze-ui/icons'
import { useConnectAccountPopup } from 'components/Onboarding/useConnectAccountPopup'
import { useExternalIntegrations } from 'components/ExportModal/ExternalIntegrations/ExternalIntegrations'
import { ExternalIntegration, ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { Account } from 'screens/BrandKit/SourceMaterials/SourceCreationModal/Account'
import { InitiateOauthTrigger } from 'components/ConnectIntegrationTrigger/InitiateOauthTrigger'
import cx from 'classnames'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { useRootStore } from 'stores/RootStore'
import { useDeleteExternalIntegration } from 'hooks/queries/externalIntegrations/useDeleteExternalIntegration'
import { SettingsDrawer } from './SettingsDrawer/SettingsDrawer'

const MailchimpRow = ({ integration }: { integration: ExternalIntegration }) => {
  const { drawersStore } = useRootStore()
  const { mutate } = useDeleteExternalIntegration(integration.id)

  const openSettingsDrawer = () => {
    drawersStore.openDrawer(SettingsDrawer, {
      onDelete: async () => {
        mutate()
        drawersStore.closeDrawer()
      },
    })
  }

  return (
    <>
      <ResponsiveDesign.Desktop>
        <div className={Styles.row}>
          <div className={Styles.rowTitle}>
            <Mailchimp size={24} />
            <Text variant="largeList">Mailchimp</Text>
          </div>
          <Account source={integration} />
        </div>
      </ResponsiveDesign.Desktop>
      <ResponsiveDesign.Mobile>
        <div className={cx(Styles.row, Styles.withAccount)}>
          <div className={Styles.account}>
            <Account source={integration} avatarSize={32} />
          </div>
          <div className={Styles.connectedContainer}>
            <div className={Styles.checkIconContainer}>
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path
                  d="M2 6.41176C2.79279 6.96078 4.37838 8.47059 5.02703 9.5C5.81982 7.85294 7.83784 4.14706 10 2.5"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <div className={Styles.connectedTextContainer}>
              <Text color="var(--green-50)" variant="smallList">
                Connected
              </Text>
              <IconButton variant="editor" icon={Settings} onPress={openSettingsDrawer} />
            </div>
          </div>
        </div>
      </ResponsiveDesign.Mobile>
    </>
  )
}

export const Email = ({ size = 'md' }: { size?: 'sm' | 'md' }) => {
  const { isMobile } = useResponsiveDesign()
  const { connect } = useConnectAccountPopup()
  const { data: integrations } = useExternalIntegrations({
    select: (data) => {
      const emailIntegrations = data.external_integrations.filter((integration) => {
        return integration.provider === ExternalIntegrationEnum.Mailchimp
      })
      return {
        external_integrations: emailIntegrations,
      }
    },
  })

  const mailchimpIntegration = integrations?.external_integrations?.[0]

  return (
    <Section title="Email" level={size === 'sm' ? 4 : 2}>
      <div className={Styles.container}>
        {mailchimpIntegration && <MailchimpRow integration={mailchimpIntegration} />}
        {!mailchimpIntegration && (
          <div className={cx(Styles.row, Styles.mailchimp)}>
            <div className={Styles.rowTitle}>
              <Mailchimp size={24} />
              <Text variant={isMobile ? 'smallList' : 'largeList'}>Mailchimp</Text>
            </div>
            <InitiateOauthTrigger popUp provider={ExternalIntegrationEnum.Mailchimp} onInitiatedOauth={connect}>
              <Button
                variant="secondary"
                size={isMobile ? 'sm' : 'lg'}
                frontIcon={LinkAngled}
                aria-label="Connect Mailchimp"
              >
                {isMobile ? 'Connect' : 'Connect Mailchimp'}
              </Button>
            </InitiateOauthTrigger>
          </div>
        )}
      </div>
    </Section>
  )
}
